import { type FC, useEffect, useMemo, useRef, useState } from "react";

import { Loader2 } from "lucide-react";

import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { Button } from "@/shared/components/ui/button";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { formatContactName } from "@/shared/utils/contactUtils";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { parseConsentEntries, parseJsonIds } from "@/shared/utils/consentUtils";
import { getRequiredAdminForms } from "@/shared/constants/adminForms";
import { computeSignoffLoop } from "@/pages/postloss/get-authorized/utils/adminSignoffUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const CONSENT_KEY = "admin_signoff_consents";

type AdminConsentOption = "co_apply" | "consent" | "uncooperative" | "cannot_locate";

interface AdminConsentEntry {
  name: string;
  status: AdminConsentOption;
}

const OPTIONS: { value: AdminConsentOption; label: (adminName: string) => string }[] = [
  { value: "co_apply", label: (n) => `Wants to apply with ${n}` },
  { value: "consent", label: () => "Agrees and will sign a consent" },
  { value: "uncooperative", label: () => "Won't sign or won't respond" },
  { value: "cannot_locate", label: () => "Can't be found after a real search" },
];

function parseConsents(raw: string | string[] | undefined): Record<string, AdminConsentEntry> {
  if (typeof raw !== "string" || !raw) return {};
  try {
    return JSON.parse(raw) as Record<string, AdminConsentEntry>;
  } catch {
    return {};
  }
}

export const AdminSignoffLoopScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  onComplete,
}) => {
  const { user, person } = useAppContext();
  const { contacts, isLoading, error: contactsError } = useUserContacts();

  const userEmail = user?.email?.toLowerCase() ?? null;
  const mode = (answers["admin_confirm_admin_mode"] as string) ?? "sole";
  const isSomeoneElse = mode === "someone_else";
  const adminName = isSomeoneElse
    ? ((answers["admin_someone_else_full_name"] as string) ?? "the administrator").split(" ")[0]
    : "you";

  const topPersonId = answers["admin_confirm_admin_top_person_id"] as string | undefined;
  const coApplicantAnswerRaw = answers["admin_confirm_admin_co_applicants"];

  const loopResult = useMemo(
    () =>
      computeSignoffLoop({
        contacts,
        adminMode: mode,
        userEmail,
        topPersonId,
        coApplicantAnswerRaw,
      }),
    [contacts, mode, userEmail, topPersonId, coApplicantAnswerRaw],
  );
  const { loopContacts } = loopResult;

  const savedConsents = useMemo(() => parseConsents(answers[CONSENT_KEY]), [answers[CONSENT_KEY]]);

  const initialIndex = useMemo(() => {
    const first = loopContacts.findIndex((c) => !savedConsents[String(c.id)]);
    return first >= 0 ? first : 0;
  }, [loopContacts, savedConsents]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const total = loopContacts.length;
  const currentContact = loopContacts[currentIndex] ?? null;
  const currentSelection = currentContact ? (savedConsents[String(currentContact.id)]?.status ?? "") : "";

  const setAnswerRef = useRef(setAnswer);
  setAnswerRef.current = setAnswer;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setAnswerRef.current("admin_signoff_loop_loading", (isLoading || !!contactsError) ? "true" : "");
    setAnswerRef.current("admin_signoff_loop_index", String(currentIndex));
    setAnswerRef.current("admin_signoff_loop_total", String(total));
    const contact = loopContacts[currentIndex];
    const hasSelection = contact ? !!savedConsents[String(contact.id)] : false;
    setAnswerRef.current("admin_signoff_loop_has_selection", hasSelection ? "true" : "");
  }, [isLoading, currentIndex, total, loopContacts, savedConsents]);

  const handleSelect = (value: string) => {
    if (!currentContact) return;
    const name = formatContactName(currentContact.first_name, currentContact.last_name);
    const entry: AdminConsentEntry = { name, status: value as AdminConsentOption };
    const updated = { ...savedConsents, [String(currentContact.id)]: entry };
    setAnswer(CONSENT_KEY, JSON.stringify(updated));
    setAnswer("admin_signoff_loop_has_selection", "true");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    );
  }

  if (contactsError) {
    return (
      <div className="flex flex-col gap-3 py-12 text-center">
        <p className="text-sm text-secondary">Could not load family contacts. Please close and try again.</p>
      </div>
    );
  }

  if (total === 0) {
    const decedentName = person?.first_name ?? "the decedent";
    const adminMode = (answers["admin_confirm_admin_mode"] as string) ?? "sole";
    const isSomeoneElse = adminMode === "someone_else";
    const proposedAdminName = isSomeoneElse
      ? ((answers["admin_someone_else_full_name"] as string) ?? "the administrator").split(" ")[0]
      : "You";
    const consents = parseConsentEntries(answers["admin_signoff_consents"]);
    const coApplicantIds = adminMode === "co" ? parseJsonIds(answers["admin_confirm_admin_co_applicants"]) : [];
    const { forms } = getRequiredAdminForms({
      contacts,
      adminMode,
      proposedAdminName: isSomeoneElse ? proposedAdminName : "You",
      proposedAdminTier: loopResult.selectedAdminTier,
      consents,
      coApplicantIds,
      extendedFamilyHeirship: false,
    });
    const hasNotice = forms.some((f) => f.formId === "A-3");
    return (
      <div className="flex flex-col gap-4">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Good news, no family sign-offs needed
        </p>
        <p className="text-secondary tracking-[-0.28px]">
          You're first in line to serve, and no one else shares your place, so no one needs to sign
          or step aside.{hasNotice
            ? ` There's one small thing left: mailing a notice to ${decedentName}'s other heirs. We'll show you exactly what to send with your forms.`
            : ""}
        </p>
      </div>
    );
  }

  const name = currentContact
    ? formatContactName(currentContact.first_name, currentContact.last_name)
    : "";
  const relationshipLabel = currentContact?.relationship
    ? getRelationshipLabel(currentContact.relationship)
    : null;

  const firstName = currentContact?.first_name ?? name;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-base font-semibold">{name}</p>
          <p className="text-sm text-secondary">
            {relationshipLabel ? `${relationshipLabel} - Same place in line as ${adminName}` : `Same place in line as ${adminName}`}
          </p>
        </div>
        <p className="text-sm text-secondary shrink-0">
          {currentIndex + 1} of {total}
        </p>
      </div>

      <p className="font-sans text-xl font-semibold leading-[30px]">
        How will {firstName} take part in {isSomeoneElse ? `${adminName}'s` : "your"} application?
      </p>

      <RadioGroup
        name="admin_signoff_consent"
        value={currentSelection}
        onValueChange={handleSelect}
        className="flex flex-col gap-3"
      >
        {OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label(isSomeoneElse ? adminName : "you")}
            selected={currentSelection === option.value}
            onClick={() => handleSelect(option.value)}
            variant="radio"
            value={option.value}
          />
        ))}
      </RadioGroup>
      <Button
        variant="brand"
        size="md"
        disabled={!currentSelection}
        onClick={() => {
          if (currentIndex < total - 1) {
            setCurrentIndex(currentIndex + 1);
          } else {
            onComplete();
          }
        }}
        className="self-end"
      >
        {currentIndex < total - 1 ? "Next person" : "Done"}
      </Button>
    </div>
  );
};

export function getAdminSignoffLoopState(answers: Record<string, string | string[]>): {
  loading: boolean;
  index: number;
  total: number;
  hasSelection: boolean;
} {
  return {
    loading: answers["admin_signoff_loop_loading"] === "true",
    index: Math.max(0, Number(answers["admin_signoff_loop_index"] ?? "0")),
    total: Number(answers["admin_signoff_loop_total"] ?? "0"),
    hasSelection: answers["admin_signoff_loop_has_selection"] === "true",
  };
}
